import { GRID_SIZE, SPAN } from './constants';
import { generationEpochStart } from './epochs';
import { cheb, key } from './geometry';
import { mulberry32, randInt, type Rng } from './rng';
import { statsFor } from './scoring';
import { beginCarve, landRegions, straitWidths, type Carve } from './terrain';
import type { Archetype, ObstacleMap, Point, Puzzle } from './types';

/** The five shapes of thought (GDD §5), in a fixed order so the weighting is stable. */
export const ARCHETYPES: readonly Archetype[] = [
  'river',
  'delta',
  'canyon',
  'archipelago',
  'radial',
];

/**
 * How often each shape comes up.
 *
 * River stays the most common (GDD §5): the crossing is the decision the game
 * taught first, and the other four are variations on it. The rest sit level
 * with each other, so no day is rare enough to feel like a bug.
 */
export const ARCHETYPE_WEIGHTS: Readonly<Record<Archetype, number>> = {
  river: 4,
  delta: 2,
  canyon: 2,
  archipelago: 2,
  radial: 2,
};

/** Keeps the archetype draw off the streams terrain and layout run on. */
const ARCHETYPE_SALT = 0x5bf03635;

/** Towers walked before pruning; the line lands at 4 or 5 once the slack is out. */
const LINE_STEPS = 5;
/** Extra steps the walk may take, so a line is never one fixed length. */
const LINE_SPREAD = 2;
/**
 * The shortest line a deal is allowed to keep, before and after pruning.
 *
 * Three towers for three houses is one tower each and nothing to trade, which
 * is a par that presses on nobody (GDD §3.3). The doc's floor of three is the
 * guarantee the game makes, not the length the generator aims for.
 */
const MIN_TOWERS = 4;
/**
 * How far a house has to live from the plant, strictest first.
 *
 * Distance is what buys the line its length: a house four cells out is one
 * tower's work, six cells is two, and a line of one tower per house is a par
 * of three with no room to trade. The ladder relaxes rather than fails, so a
 * cramped corner of the board still gets a house instead of a re-roll.
 */
const HOUSE_FROM_PLANT = [6, 5, 4, 3];

/** How far houses stay from each other, strictest first, so three drops stay three decisions. */
const HOUSE_APART = [4, 3];
/** How far apart canyon's two passes have to sit to read as two ways through. */
const PASS_APART = 3;
/** How far radial's aim swings around the plant, in radians — two thirds of a turn. */
const RADIAL_SWEEP = 4.2;
/** The narrowest angle radial accepts between two houses, in radians (70°). */
const RADIAL_GAP = 1.22;
/**
 * Why radial is not the pure junction day the design wanted.
 *
 * Angle alone does not force a branch: three houses spread around a plant but
 * close to it are reachable by one line that curls past all of them, which is
 * why radial's references branch on ~73% of days against the river's ~100%.
 * Distance is the missing ingredient — requiring all three houses 5 cells out
 * lifts branching to 96% — but on a 9x9 with a central plant, three houses
 * that far out, 4 apart and 70 degrees apart, is roughly a 1-in-100 draw:
 * measured, it leaves 60% of days undealt after 60 attempts.
 *
 * So the constraint is left off deliberately, and the claim was corrected in
 * the GDD instead. Rejection sampling cannot buy this; a bigger board (the XL
 * grid on the roadmap) or a radial-specific layout stage that places the
 * houses first and walks the line to them can. Do not re-add a reach floor
 * here without one of those.
 */

/** Straight-line distance in cells. Direction only; wire is measured elsewhere. */
const reach = (a: Point, b: Point): number => Math.hypot(a.r - b.r, a.c - b.c);

/** Bearing of a cell from another, for the shapes that care which way the pull goes. */
const bearing = (from: Point, to: Point): number => Math.atan2(to.r - from.r, to.c - from.c);

/**
 * A carved board plus the placement rules the shape owns.
 *
 * Terrain and layout are one decision (GDD §5): a river the plant sits east of
 * is not a crossing day, so the shape that draws the water also says where the
 * plant may stand, where the line is walking, and who has to live across it.
 */
interface Ground {
  obstacles: ObstacleMap;
  /** Is this cell open land a tower, a house or the plant could stand on? */
  free: (p: Point) => boolean;
  /** Every cell the plant may stand on. */
  plantSites: Point[];
  /**
   * Where the line is walking, `t` running 0 to 1 across the walk. Most shapes
   * aim at a fixed far side; canyon aims at its pass first and past it after,
   * and radial swings the aim around the plant, which is what turns one line
   * into three directions.
   */
  aim: (plant: Point, t: number) => Point;
  /** The shape's own rule about where people live, read once they are placed. */
  accept: (plant: Point, houses: Point[]) => boolean;
}

/** Draws one board of a shape, or gives up and lets the caller re-roll. */
type Shape = (rnd: Rng) => Ground | null;

/** Every cell of the board, in scan order. */
function allCells(): Point[] {
  const cells: Point[] = [];
  for (let r = 0; r < GRID_SIZE; r++) for (let c = 0; c < GRID_SIZE; c++) cells.push({ r, c });
  return cells;
}

/** The first and last water column on each row — the banks a channel leaves behind. */
function banks(obstacles: ObstacleMap): { west: number[]; east: number[] } {
  const west: number[] = [];
  const east: number[] = [];
  for (let r = 0; r < GRID_SIZE; r++) {
    let w = GRID_SIZE;
    let e = -1;
    for (let c = 0; c < GRID_SIZE; c++) {
      if (obstacles[r + ',' + c] === 'water') {
        w = Math.min(w, c);
        e = Math.max(e, c);
      }
    }
    west.push(w);
    east.push(e);
  }
  return { west, east };
}

/** Scatter lone buttes and the odd two-cell ridge. Never a wall: these only decorate. */
function scatterHighGround(g: Carve, rnd: Rng, count: number): void {
  const ri = (n: number) => randInt(rnd, n);
  for (let i = 0; i < count; i++) {
    const r = ri(GRID_SIZE);
    const c = ri(GRID_SIZE);
    g.put(r, c, 'mtn');
    if (rnd() < 0.35) g.put(r + (rnd() < 0.5 ? 1 : -1), c + (rnd() < 0.5 ? 1 : 0), 'mtn');
  }
}

/**
 * River: one channel wanders top to bottom, its bends leaving 2-wide elbows,
 * and often a lake where the crossing is off entirely. Plant west, someone
 * east: the crossing day.
 */
const carveRiver: Shape = (rnd) => {
  const ri = (n: number) => randInt(rnd, n);
  const g = beginCarve(GRID_SIZE);

  const riverCol: number[] = [];
  let c = 3 + ri(3);
  for (let r = 0; r < GRID_SIZE; r++) {
    g.put(r, c, 'water');
    riverCol.push(c);
    if (r === GRID_SIZE - 1) break;
    const nc = Math.min(GRID_SIZE - 3, Math.max(2, c + ri(3) - 1));
    if (nc !== c) g.put(r, nc, 'water'); // the bend's elbow, 2 wide and uncrossable
    c = nc;
  }

  // A lake runs the channel 2 wide for a stretch, so part of the bank is off.
  if (rnd() < 0.55) {
    const side = rnd() < 0.5 ? 1 : -1;
    const start = 1 + ri(GRID_SIZE - 4);
    for (let r = start; r < Math.min(GRID_SIZE, start + 2 + ri(2)); r++) {
      g.put(r, riverCol[r] + side, 'water');
    }
  }
  scatterHighGround(g, rnd, 1 + ri(3));

  const { west, east } = banks(g.obstacles);
  const drift = ri(5) - 2;
  return {
    obstacles: g.obstacles,
    free: g.free,
    plantSites: allCells().filter((p) => p.c < Math.min(west[p.r], 3) && g.free(p)),
    aim: (plant) => ({
      r: Math.max(0, Math.min(GRID_SIZE - 1, plant.r + drift)),
      c: GRID_SIZE - 1,
    }),
    accept: (_plant, houses) => houses.some((h) => h.c > east[h.r]),
  };
};

/**
 * Delta: the channel forks around an island of land and runs two branches to
 * the sea. Houses split across the branches, so the day is two crossings or a
 * long way round to make it one.
 */
const carveDelta: Shape = (rnd) => {
  const ri = (n: number) => randInt(rnd, n);
  const g = beginCarve(GRID_SIZE);

  const fork = 2 + ri(3);
  let c = 3 + ri(3);
  for (let r = 0; r <= fork; r++) {
    g.put(r, c, 'water');
    if (r === fork) break;
    const nc = Math.min(GRID_SIZE - 3, Math.max(2, c + ri(3) - 1));
    if (nc !== c) g.put(r, nc, 'water');
    c = nc;
  }

  // Two branches leaving the fork, the island of land widening between them.
  const left: number[] = [];
  const right: number[] = [];
  let lc = c;
  let rc = c;
  for (let r = fork + 1; r < GRID_SIZE; r++) {
    if (rnd() < 0.8) lc = Math.max(1, lc - 1);
    if (rnd() < 0.8) rc = Math.min(GRID_SIZE - 2, rc + 1);
    if (rc - lc < 2) rc = Math.min(GRID_SIZE - 2, lc + 2);
    g.put(r, lc, 'water');
    g.put(r, rc, 'water');
    left.push(lc);
    right.push(rc);
  }
  scatterHighGround(g, rnd, 1 + ri(2));

  const { west } = banks(g.obstacles);
  /** West bank, the island between the branches, or the far shore. */
  const zone = (p: Point): 'west' | 'island' | 'east' => {
    if (p.r <= fork) return p.c < west[p.r] ? 'west' : 'east';
    const i = p.r - fork - 1;
    if (p.c < left[i]) return 'west';
    return p.c > right[i] ? 'east' : 'island';
  };

  return {
    obstacles: g.obstacles,
    free: g.free,
    plantSites: allCells().filter((p) => zone(p) === 'west' && p.c < 3 && g.free(p)),
    aim: (plant) => ({ r: plant.r, c: GRID_SIZE - 1 }),
    accept: (_plant, houses) => {
      const zones = new Set(houses.map(zone));
      return zones.size >= 2 && (zones.has('island') || zones.has('east'));
    },
  };
};

/**
 * Canyon: a high range walls the map, doubled so no wire clears it, with one
 * or two passes cut through. Plant one side, houses beyond: the pass is the
 * whole game.
 *
 * The wall runs dead straight and thickens outward rather than wandering. A
 * range that wobbles by a column opens a diagonal leak two rows away, which
 * hands the player a second pass the map never drew.
 */
const carveCanyon: Shape = (rnd) => {
  const ri = (n: number) => randInt(rnd, n);
  const g = beginCarve(GRID_SIZE);

  const vertical = rnd() < 0.6;
  /** `i` runs along the wall, `j` across it. */
  const at = (i: number, j: number): Point => (vertical ? { r: i, c: j } : { r: j, c: i });
  const across = (p: Point): number => (vertical ? p.c : p.r);

  const lo = 3 + ri(3);
  // Two ways through, always, and far enough apart to be two decisions.
  //
  // One pass funnels every route, so the seed line is nearly forced and there
  // is nothing left to tune: measured over a simulated year, single-pass
  // canyons were the flattest boards the generator made, and the shape failed
  // its own quality gate on that alone (GDD §5.3). Adjacent passes are the
  // same failure wearing a disguise — they read as one wider gap.
  const passes = new Set<number>();
  const first = 1 + ri(GRID_SIZE - 2);
  passes.add(first);
  for (let guard = 0; passes.size < 2 && guard < 24; guard++) {
    const cand = 1 + ri(GRID_SIZE - 2);
    if (Math.abs(cand - first) >= PASS_APART) passes.add(cand);
  }
  if (passes.size < 2) return null;

  for (let i = 0; i < GRID_SIZE; i++) {
    if (passes.has(i)) continue;
    const p1 = at(i, lo);
    const p2 = at(i, lo + 1);
    g.put(p1.r, p1.c, 'mtn');
    g.put(p2.r, p2.c, 'mtn');
    if (rnd() < 0.4) {
      const p3 = at(i, lo + (rnd() < 0.5 ? -1 : 2));
      g.put(p3.r, p3.c, 'mtn');
    }
  }
  scatterHighGround(g, rnd, ri(3));

  const passRow = [...passes][0];
  const beyond = Math.min(GRID_SIZE - 1, lo + 4 + ri(2));
  return {
    obstacles: g.obstacles,
    free: g.free,
    plantSites: allCells().filter((p) => across(p) < lo && g.free(p)),
    // Walk to the pass first: a line that aims straight at the far side just
    // grinds along the rock face.
    aim: (_plant, t) => (t < 0.45 ? at(passRow, lo) : at(passRow, beyond)),
    // Two houses beyond the wall, not one: with a single house across, both
    // passes serve the same destination and the second is decoration. Two
    // gives the far pass someone to be closer to.
    accept: (_plant, houses) => houses.filter((h) => across(h) > lo + 1).length >= 2,
  };
};

/**
 * Archipelago: open water and land islands, cut by straits a player can read
 * by width alone. The full-length channels are 1 wide and crossable; one sound
 * is 2 wide and is not, so the far shore stays visible and out of reach.
 *
 * Widths are drawn deliberately and then measured (GDD §5): a channel that
 * came out 2 wide because two cuts landed side by side would teach the span
 * rule wrong, so the whole board is thrown away instead.
 */
const carveArchipelago: Shape = (rnd) => {
  const ri = (n: number) => randInt(rnd, n);
  const g = beginCarve(GRID_SIZE);

  // Cuts run the full length of the board, so a width is the width the player
  // sees everywhere along it. Two cuts on the same axis stay two cells apart:
  // side by side they would read as one wider channel nobody drew.
  const cut = (vertical: boolean, at: number, width: number): void => {
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let w = 0; w < width; w++) {
        const p = vertical ? { r: i, c: at + w } : { r: at + w, c: i };
        g.put(p.r, p.c, 'water');
      }
    }
  };
  const claimed: Record<'v' | 'h', number[]> = { v: [], h: [] };
  const clear = (axis: 'v' | 'h', at: number, width: number): boolean =>
    claimed[axis].every((t) => t < at - 1 || t > at + width);

  // The sound: 2 wide and uncrossable, close enough to an edge that the shore
  // it cuts off reads as a far shore rather than half the board.
  const soundVertical = rnd() < 0.5;
  const soundAt = rnd() < 0.5 ? 1 : GRID_SIZE - 3;
  cut(soundVertical, soundAt, 2);
  claimed[soundVertical ? 'v' : 'h'].push(soundAt, soundAt + 1);

  // Straits: 1 wide, so a tower on either bank spans them.
  for (let n = 2 + ri(2); n > 0; n--) {
    const vertical = rnd() < 0.5;
    const axis = vertical ? 'v' : 'h';
    const at = 1 + ri(GRID_SIZE - 2);
    if (!clear(axis, at, 1)) continue;
    claimed[axis].push(at);
    cut(vertical, at, 1);
  }
  scatterHighGround(g, rnd, ri(3));

  if (straitWidths(g.obstacles, GRID_SIZE).some((w) => w > 2)) return null;

  const regions = landRegions(g.obstacles, GRID_SIZE);
  if (regions.length < 2) return null;
  const regionAt = new Map<string, number>();
  regions.forEach((cells, i) => cells.forEach((p) => regionAt.set(key(p), i)));

  return {
    obstacles: g.obstacles,
    free: g.free,
    plantSites: regions[0],
    // The far corner keeps the line island-hopping instead of circling home.
    aim: (plant) => {
      const corners = [
        { r: 0, c: 0 },
        { r: 0, c: GRID_SIZE - 1 },
        { r: GRID_SIZE - 1, c: 0 },
        { r: GRID_SIZE - 1, c: GRID_SIZE - 1 },
      ];
      return corners.reduce((best, p) => (reach(p, plant) > reach(best, plant) ? p : best));
    },
    accept: (plant, houses) =>
      houses.some((h) => regionAt.get(key(h)) !== regionAt.get(key(plant))),
  };
};

/**
 * Radial: sparse ground, no barrier at all. The plant sits near the middle and
 * three houses pull three different ways, so the only decision left is where
 * the branch point goes — the pure junction day.
 */
const carveRadial: Shape = (rnd) => {
  const ri = (n: number) => randInt(rnd, n);
  const g = beginCarve(GRID_SIZE);

  // Short ridges, never doubled and never long enough to wall anything off.
  for (let n = 1 + ri(2); n > 0; n--) {
    let r = 1 + ri(GRID_SIZE - 2);
    let c = 1 + ri(GRID_SIZE - 2);
    const dr = ri(3) - 1;
    const dc = dr === 0 ? (rnd() < 0.5 ? 1 : -1) : ri(3) - 1;
    for (let i = 0; i < 2 + ri(2); i++) {
      g.put(r, c, 'mtn');
      r += dr;
      c += dc;
    }
  }
  scatterHighGround(g, rnd, 2 + ri(3));

  const mid = (GRID_SIZE - 1) / 2;
  const spin = rnd() < 0.5 ? 1 : -1;
  const base = rnd() * Math.PI * 2;

  return {
    obstacles: g.obstacles,
    free: g.free,
    plantSites: allCells().filter(
      (p) => Math.abs(p.r - mid) <= 1.5 && Math.abs(p.c - mid) <= 1.5 && g.free(p),
    ),
    // The aim swings around the plant as the walk goes on, so the line curls
    // instead of running off one side and taking every house with it.
    aim: (plant, t) => {
      const a = base + spin * t * RADIAL_SWEEP;
      // The arc stays wide: a house sits within span of a tower, so pulling
      // the arc in pulls the houses in with it, and close houses are exactly
      // what lets one spine serve all three.
      return { r: plant.r + Math.round(Math.sin(a) * 4), c: plant.c + Math.round(Math.cos(a) * 4) };
    },
    accept: (plant, houses) => {
      const angles = houses.map((h) => bearing(plant, h)).sort((a, b) => a - b);
      const gaps = angles.map((a, i) => {
        const next = i === angles.length - 1 ? angles[0] + Math.PI * 2 : angles[i + 1];
        return next - a;
      });
      return Math.min(...gaps) >= RADIAL_GAP;
    },
  };
};

const SHAPES: Readonly<Record<Archetype, Shape>> = {
  river: carveRiver,
  delta: carveDelta,
  canyon: carveCanyon,
  archipelago: carveArchipelago,
  radial: carveRadial,
};

/**
 * Walk the line: towers at exactly span from each other, aimed at whatever the
 * shape is pointing them at, with enough jitter that no two days trace the
 * same path.
 */
function walkLine(g: Ground, plant: Point, rnd: Rng): Point[] {
  const ri = (n: number) => randInt(rnd, n);
  const steps = LINE_STEPS + ri(LINE_SPREAD);
  const towers: Point[] = [];
  const taken = new Set([key(plant)]);
  let cur = plant;

  for (let i = 0; i < steps; i++) {
    const target = g.aim(plant, i / steps);
    const cands: { p: Point; s: number }[] = [];
    for (let dr = -SPAN; dr <= SPAN; dr++) {
      for (let dc = -SPAN; dc <= SPAN; dc++) {
        if (Math.max(Math.abs(dr), Math.abs(dc)) !== SPAN) continue;
        const p = { r: cur.r + dr, c: cur.c + dc };
        if (!g.free(p) || taken.has(key(p))) continue;
        cands.push({ p, s: reach(cur, target) - reach(p, target) + rnd() * 1.2 });
      }
    }
    if (!cands.length) break;
    cands.sort((a, b) => b.s - a.s);
    const pick = cands[Math.min(ri(2), cands.length - 1)].p;
    towers.push(pick);
    taken.add(key(pick));
    cur = pick;
  }
  return towers;
}

/**
 * Hang the three houses off the line — but off different parts of it, and
 * pulling away from it.
 *
 * This is the whole point of stage 2 (GDD §5). Houses strung along the end of
 * the line make the line the answer, and a day with one answer has nothing to
 * tune; houses that pull sideways from three anchors put the branch point in
 * play.
 */
function placeHouses(g: Ground, plant: Point, towers: Point[], rnd: Rng): Point[] | null {
  const ri = (n: number) => randInt(rnd, n);
  const last = towers[towers.length - 1];
  const axis = bearing(plant, last);
  /** How far off the line's own direction a cell sits. */
  const offAxis = (p: Point): number =>
    Math.abs(Math.sin(bearing(plant, p) - axis)) * reach(plant, p);

  const early = ri(2);
  const mid = Math.floor(towers.length / 2);
  const anchors = [towers.length - 1, mid, early === mid ? 0 : early].map((i) => towers[i]);

  const houses: Point[] = [];
  const taken = new Set([key(plant), ...towers.map(key)]);
  for (const anchor of anchors) {
    let best: Point | null = null;
    for (const minPlant of HOUSE_FROM_PLANT) {
      for (const minApart of HOUSE_APART) {
        let bestScore = -Infinity;
        for (let dr = -SPAN; dr <= SPAN; dr++) {
          for (let dc = -SPAN; dc <= SPAN; dc++) {
            const p = { r: anchor.r + dr, c: anchor.c + dc };
            if (!g.free(p) || taken.has(key(p))) continue;
            if (cheb(p, plant) < minPlant) continue;
            if (houses.some((h) => cheb(h, p) < minApart)) continue;
            const apart = houses.length ? Math.min(...houses.map((h) => reach(h, p))) : 0;
            const s = offAxis(p) + apart * 0.5 + rnd() * 0.9;
            if (s > bestScore) {
              bestScore = s;
              best = p;
            }
          }
        }
        if (best) break;
      }
      if (best) break;
    }
    if (!best) return null;
    houses.push(best);
    taken.add(key(best));
  }
  return houses;
}

/**
 * Drop the dead weight from a walked line: any tower that can go without
 * darkening a house and without costing wire to lose.
 *
 * Par is the length of the generator's own line (GDD §3.3), so a tower the
 * line carries for nothing is a budget that lies. A tower whose removal
 * lengthens the run stays, because that one is doing work — and what is left
 * is a line no single removal improves, which puts the day's tuning move where
 * the deception band looks for it: in relocating a tower, not deleting one.
 */
function prune(puzzle: Puzzle, towers: Point[]): Point[] {
  let line = towers;
  let wire = statsFor(puzzle, line).wire;
  for (let round = 0; round < towers.length; round++) {
    let cut: { line: Point[]; wire: number } | null = null;
    for (let i = line.length - 1; i >= 0 && !cut; i--) {
      const rest = line.filter((_, j) => j !== i);
      const s = statsFor(puzzle, rest);
      if (s.ok && s.wire <= wire + 1e-9) cut = { line: rest, wire: s.wire };
    }
    if (!cut) break;
    line = cut.line;
    wire = cut.wire;
  }
  return line;
}

/** Weighted draw, never landing on `prev` — the no-repeat rule, as a filter. */
function draw(rnd: Rng, prev: Archetype | null): Archetype {
  const pool = ARCHETYPES.filter((a) => a !== prev);
  const total = pool.reduce((sum, a) => sum + ARCHETYPE_WEIGHTS[a], 0);
  let roll = rnd() * total;
  for (const a of pool) {
    roll -= ARCHETYPE_WEIGHTS[a];
    if (roll < 0) return a;
  }
  return pool[pool.length - 1];
}

/** The shape a practice grid takes: straight from its own seed, nothing to avoid. */
export function archetypeForSeed(seed: number): Archetype {
  return draw(mulberry32(seed ^ ARCHETYPE_SALT), null);
}

/**
 * The shape a daily grid takes.
 *
 * Walked forward from the epoch's first grid, because "never the same as the
 * previous day" is a property of the sequence rather than of a day number: the
 * walk keeps only yesterday's answer, so it costs one draw per grid since the
 * epoch opened and stays well under a millisecond for grids years out.
 */
export function archetypeForDay(day: number): Archetype {
  const start = generationEpochStart(1);
  let prev: Archetype | null = null;
  let cur: Archetype = ARCHETYPES[0];
  for (let d = start; d <= Math.max(day, start); d++) {
    cur = draw(mulberry32(d ^ ARCHETYPE_SALT), prev);
    prev = cur;
  }
  return cur;
}

/**
 * One deterministic layout attempt for a shape: carve the ground, stand the
 * plant on it, walk the line, and hang the houses off it.
 *
 * Returns `null` when the land won't cooperate — no plant site on the right
 * bank, a line that walks into a corner, nowhere left to live — and the caller
 * re-rolls with a perturbed seed.
 */
export function dealArchetype(archetype: Archetype, mix: number): Puzzle | null {
  const rnd = mulberry32(mix);
  const g = SHAPES[archetype](rnd);
  if (!g) return null;

  const sites = g.plantSites.filter((p) => g.free(p));
  if (!sites.length) return null;
  const plant = sites[randInt(rnd, sites.length)];

  const towers = walkLine(g, plant, rnd);
  if (towers.length < MIN_TOWERS) return null;

  const houses = placeHouses(g, plant, towers, rnd);
  if (!houses || !g.accept(plant, houses)) return null;

  const draft: Puzzle = {
    plant,
    houses,
    obstacles: g.obstacles,
    par: towers.length,
    seedTowers: towers,
    archetype,
  };
  if (!statsFor(draft, towers).ok) return null;

  const line = prune(draft, towers);
  if (line.length < MIN_TOWERS) return null;
  return { ...draft, par: line.length, seedTowers: line };
}
